<template>
  <div class="left-menu-wrapper fd-shell__sidebar" :class="{ 'is-collapsed': collapsed }">
    <v-icon
      class="toggle-icon"
      :icon="collapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
      :style="toggleStyle"
      @click="toggle"
    />

    <div
      v-if="!collapsed"
      class="fd-sidebar-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      @pointerdown.prevent="onResizePointerDown"
    />

    <div :class="['sidebar', { close: collapsed }]">
      <div class="logo-div" v-if="!collapsed">
        <div class="img-wrap">
          <v-img aspect-ratio="16/9" :src="logo.expanded" alt="logo" />
        </div>
      </div>
      <div class="logo-div2" v-else>
        <div class="img-wrap2">
          <v-img :src="logo.collapsed" alt="logo" />
        </div>
      </div>

      <ul class="nav-list">
        <li v-for="item in items" :key="item.id" :class="{ show: isOpen(item.id) }">
          <div
            v-if="item.children"
            class="icon-link"
            :class="{ 'active-item-sub': isActive(item) }"
            @click="toggleSubmenu(item.id)"
          >
            <div class="link">
              <img :src="isActive(item) ? item.activeIcon || item.icon : item.icon" alt="" />
              <span class="link-name">{{ item.title }}</span>
              <v-icon
                class="submenu-arrow"
                :icon="isOpen(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                color="#E5E7EB"
                size="18"
              />
            </div>
          </div>
          <ul v-if="item.children" :class="{ show: isOpen(item.id) }" class="sub-menu" @click.stop>
            <li v-for="child in item.children" :key="child.id" :class="{ 'sub-item-active': isChildActive(child) }">
              <router-link
                :to="child.to"
                class="sub-link"
                @click="markActiveMenu(item.title)"
              >
                <!-- <img :src="isChildActive(child) ? child.activeIcon || child.icon : child.icon" alt="" /> -->
                <span :class="{ 'active-text-child': isChildActive(child) }">
                  {{ child.title }}
                </span>
              </router-link>
            </li>
          </ul>
          <div v-else class="icon-link" :class="{ 'active-item': isActive(item) }">
            <router-link :to="item.to" class="link" @click="markActiveMenu(item.title)">
              <img :src="isActive(item) ? item.activeIcon || item.icon : item.icon" alt="" />
              <span class="link-name" :class="{ 'active-text': isActive(item) }">
                {{ item.title }}
              </span>
            </router-link>
          </div>
        </li>
      </ul>

      <div class="sidebar-bottom">
        <div v-if="showSettings" class="settings-block">
          <div
            class="icon-link settings-link"
            :class="{ 'active-item-sub': settingsOpen || settingsChildActive }"
            @click="toggleSettings"
          >
            <div class="link">
              <img :src="settingsIcon" alt="" width="24" height="24" />
              <span class="link-name" v-if="!collapsed">Settings</span>
              <v-icon
                v-if="!collapsed"
                class="submenu-arrow"
                :icon="settingsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                color="#E5E7EB"
                size="18"
              />
            </div>
          </div>
          <ul v-if="settingsOpen && !collapsed" class="settings-sub-menu">
            <li v-for="link in settingsLinks" :key="link.id" :class="{ 'sub-item-active': isSettingsLinkActive(link) }">
              <router-link :to="link.to" class="sub-link">
                <!-- <img :src="link.icon" alt="" width="24" height="24" /> -->
                <span :class="{ 'active-text-child': isSettingsLinkActive(link) }">
                  {{ link.title }}
                </span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- <div v-if="showUsedSpace && !collapsed" class="fd-used-space">
          <div class="fd-used-space__title">Used space</div>
          <p class="fd-used-space__body">
            Your team has used 80% of your available space. Need more?
          </p>
          <div class="fd-used-space__bar" aria-hidden="true">
            <span />
          </div>
          <div class="fd-used-space__actions">
            <button type="button" @click="dismissUsedSpace">Dismiss</button>
            <button type="button" class="fd-used-space__upgrade">Upgrade plan</button>
          </div>
        </div> -->

        <v-menu
          location="top start"
          :offset="12"
          v-model="profileMenuOpen"
          :close-on-content-click="false"
          content-class="profile-menu-overlay"
        >
          <template #activator="{ props }">
            <div class="profile-row" :class="{ close: collapsed }" v-bind="props">
              <v-avatar size="40" class="profile-avatar">
                <v-img :src="profileImage" alt="profile" />
              </v-avatar>
              <div class="profile-meta" v-if="!collapsed">
                <span class="profile-name">{{ userFullName }}</span>
                <span class="profile-org">{{ tenantName || "Fluidsdata" }}</span>
              </div>
              <img
                v-if="!collapsed"
                :src="profileArrowIcon"
                alt=""
                class="profile-row-icon"
              />
            </div>
          </template>
          <v-card class="profile-menu-card" elevation="0">
            <div class="profile-menu-head">
              <div class="profile-menu-avatar-wrap">
                <v-avatar size="56" rounded="12" class="profile-menu-avatar">
                  <v-img :src="profileImage" alt="profile" cover />
                </v-avatar>
                <img :src="microsoftIcon" alt="" class="profile-menu-sso" width="16" height="16" />
              </div>
              <div class="profile-menu-identity">
                <div class="profile-menu-name">{{ userFullName }}</div>
                <div class="profile-menu-email">{{ userEmail }}</div>
              </div>
            </div>

            <label class="profile-menu-label">Tenant</label>
            <v-select
              class="profile-menu-tenant"
              :model-value="tenantName"
              :items="tenantOptions"
              placeholder="Select tenant"
              hide-details
              variant="solo"
              density="comfortable"
              menu-icon="mdi-chevron-down"
              @update:modelValue="onSelectTenant"
            />

            <div class="profile-menu-row">
              <span>Theme</span>
              <button
                type="button"
                class="profile-menu-theme"
                :class="{ 'is-dark': isDarkMode }"
                :aria-pressed="isDarkMode"
                @click="toggleDarkMode"
              >
                <span class="profile-menu-theme-icon is-sun" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="1.5" />
                    <path
                      d="M12 3v1.6M12 19.4V21M4.22 4.22l1.13 1.13M18.65 18.65l1.13 1.13M3 12h1.6M19.4 12H21M4.22 19.78l1.13-1.13M18.65 5.35l1.13-1.13"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>
                <span class="profile-menu-theme-icon is-moon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 15.2A8.2 8.2 0 1 1 8.8 4 6.6 6.6 0 0 0 20 15.2z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>

            <button type="button" class="profile-menu-row" @click="openChangePassword">
              <span>Change Password</span>
              <img :src="passwordIcon" alt="" class="profile-menu-action-icon" width="24" height="24" />
            </button>

            <div class="profile-menu-divider" />

            <button type="button" class="profile-menu-row profile-menu-row--logout" @click="logout">
              <span>Logout</span>
              <svg class="profile-menu-action-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15.1 7.56C14.79 3.96 12.94 2.49 8.89 2.49H8.76C4.29 2.49 2.5 4.28 2.5 8.75V15.27C2.5 19.74 4.29 21.53 8.76 21.53H8.89C12.91 21.53 14.76 20.08 15.09 16.54" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 12H20.38" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.15 8.65L21.5 12L18.15 15.35" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </v-card>
        </v-menu>
      </div>
    </div>

    <ChangePassword
      v-if="passwordDrawerOpen"
      v-model="passwordDrawerOpen"
      :edit-user="passwordUser"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { eventBus } from "@/eventBus";
import { resizeAllPlotlyCharts } from "@/utils/plotlyResizeAll";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useTenant } from "@app/composables/useTenant";
import { useAuth } from "@app/composables/useAuth";
import { useDarkMode } from "@app/composables/useDarkMode";
import { useNavigation } from "@app/composables/useNavigation";
import { useSidebar } from "@app/composables/useSidebar";
import { fetchMetaData } from "@/utils/cache";
import ChangePassword from "./Users/ChangePassword.vue";

const OPEN_SUBMENUS_KEY = "leftMenuOpenSubmenus";
const SETTINGS_OPEN_KEY = "leftMenuSettingsOpen";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { tenantId, user, userFullName, tenantName } = useTenant();
const { logout } = useAuth();
const { isDarkMode, toggleDarkMode } = useDarkMode();
const { items, settingsLinks, showSettings, isActive, isChildActive, markActiveMenu } =
  useNavigation();
const { collapsed, toggle, widthPx, setExpandedWidth } = useSidebar();

const resizeState = { active: false, startX: 0, startWidth: 0 };

const profileMenuOpen = ref(false);
const passwordDrawerOpen = ref(false);
const tenantOptions = computed(() =>
  (store.getters["tenantId/getTenantData"] || []).map((tenant) => tenant.name)
);
const userEmail = computed(() => user.value?.username || user.value?.email || "");
const passwordUser = computed(() => {
  const current = user.value || {};
  return {
    ...current,
    id: current.id || current.ID || current._id,
  };
});

const USED_SPACE_KEY = "fd-used-space-dismissed";

const settingsOpen = ref(localStorage.getItem(SETTINGS_OPEN_KEY) === "1");
const openSubmenus = ref(readOpenSubmenus());
const usedSpaceDismissed = ref(localStorage.getItem(USED_SPACE_KEY) === "1");
const showUsedSpace = computed(() => !usedSpaceDismissed.value);

const settingsIcon = require("@/assets/Svg/setting-2.svg");
const defaultProfileImage = require("@/assets/Images/Navbar/profile.png");
const profileImage = computed(() => user.value?.profileImage || defaultProfileImage);
const profileArrowIcon = require("@/assets/Svg/Icons/arrow-right.svg");
const passwordIcon = require("@/assets/Svg/Icons/password.svg");
const microsoftIcon = require("@/assets/Svg/Icons/microsoft.svg");
const logo = {
  expanded: require("@/assets/Images/LeftMenu/logo-dark.png"),
  collapsed: require("@/assets/Images/LeftMenu/logoicon-dark.png"),
};

const toggleStyle = computed(() => ({
  left: collapsed.value ? "48px" : `${Math.max(48, widthPx.value - 16)}px`,
}));

function onResizePointerMove(event) {
  if (!resizeState.active) return;
  const delta = event.clientX - resizeState.startX;
  setExpandedWidth(resizeState.startWidth + delta);
}

function onResizePointerUp() {
  if (!resizeState.active) return;
  resizeState.active = false;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  document.removeEventListener("pointermove", onResizePointerMove);
  document.removeEventListener("pointerup", onResizePointerUp);
  eventBus.emit("resizeChart");
  resizeAllPlotlyCharts();
}

function onResizePointerDown(event) {
  resizeState.active = true;
  resizeState.startX = event.clientX;
  resizeState.startWidth = widthPx.value;
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  document.addEventListener("pointermove", onResizePointerMove);
  document.addEventListener("pointerup", onResizePointerUp);
}

const settingsChildActive = computed(() => {
  const path = route.path || "";
  return path.includes("/users") || path.includes("/roles");
});

function readOpenSubmenus() {
  try {
    const raw = localStorage.getItem(OPEN_SUBMENUS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function isOpen(id) {
  return openSubmenus.value.includes(id);
}

function toggleSubmenu(id) {
  const item = items.value.find((entry) => entry.id === id);
  const childActive = item?.children?.some((child) => isChildActive(child));
  if (openSubmenus.value.includes(id)) {
    if (childActive) return;
    openSubmenus.value = openSubmenus.value.filter((key) => key !== id);
  } else {
    openSubmenus.value = [...openSubmenus.value, id];
  }
  localStorage.setItem(OPEN_SUBMENUS_KEY, JSON.stringify(openSubmenus.value));
}

function isSettingsLinkActive(link) {
  return (route.path || "").startsWith(link.to);
}

function toggleSettings() {
  if (settingsOpen.value && settingsChildActive.value) return;
  settingsOpen.value = !settingsOpen.value;
  localStorage.setItem(SETTINGS_OPEN_KEY, settingsOpen.value ? "1" : "0");
}

function ensureSubmenuOpen(id) {
  if (openSubmenus.value.includes(id)) return;
  openSubmenus.value = [...openSubmenus.value, id];
  localStorage.setItem(OPEN_SUBMENUS_KEY, JSON.stringify(openSubmenus.value));
}

function dismissUsedSpace() {
  usedSpaceDismissed.value = true;
  localStorage.setItem(USED_SPACE_KEY, "1");
}

function openChangePassword() {
  profileMenuOpen.value = false;
  passwordDrawerOpen.value = true;
}

async function onSelectTenant(selectedValue) {
  const selectedTenant = (store.getters["tenantId/getTenantData"] || []).find(
    (tenant) => tenant.name === selectedValue
  );
  if (!selectedTenant) return;

  const { name, ID } = selectedTenant;
  if (ID === tenantId.value) {
    return;
  }

  localStorage.removeItem("lastVisitedDashboard");
  localStorage.removeItem("savedChartData");
  localStorage.removeItem("lastFilterId");
  localStorage.removeItem("activeMenu");
  localStorage.setItem("tenantName", name);
  store.commit("tenantId/SET_SELECTED_TENANT", { name, ID });

  try {
    await fetchMetaData(ID);
  } catch {
    void 0;
  }

  profileMenuOpen.value = false;
  const segments = (route.path || "").split("/").filter(Boolean);
  const stayOnPvt = segments[1] === "pvt-prediction";
  await router.push(stayOnPvt ? `/${ID}/pvt-prediction` : `/${ID}/data-management`);
}

onMounted(() => {
  if (localStorage.getItem("authToken")) {
    store.dispatch("tenantId/fetchTenants");
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("pointermove", onResizePointerMove);
  document.removeEventListener("pointerup", onResizePointerUp);
});

watch(profileMenuOpen, (open) => {
  if (open && localStorage.getItem("authToken")) {
    store.dispatch("tenantId/fetchTenants");
  }
});

watch(
  () => route.path,
  (path) => {
    items.value.forEach((item) => {
      if (item.children?.some((child) => isChildActive(child))) {
        ensureSubmenuOpen(item.id);
      }
    });
    if (path.includes("/users") || path.includes("/roles")) {
      settingsOpen.value = true;
      localStorage.setItem(SETTINGS_OPEN_KEY, "1");
    }
  },
  { immediate: true }
);
</script>
